'use client';

import { useState, useEffect, useRef } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { Store, Copy, Check, RotateCcw, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';
import {
  SHOPIFY_PLANS,
  SHOPIFY_DATA_UPDATED,
} from '@/lib/tools/marketplace-data';

interface ShopifyCalculatorProps {
  locale: Locale;
  content: ToolContent;
}

export function ShopifyCalculator({ locale, content }: ShopifyCalculatorProps) {
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [avgOrderValue, setAvgOrderValue] = useState('');
  const [monthlyOrders, setMonthlyOrders] = useState('100');
  const [useShopifyPayments, setUseShopifyPayments] = useState(true);
  const [purchaseCostPercent, setPurchaseCostPercent] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const hasLoggedRef = useRef(false);

  const labels = content.labels;

  useEffect(() => {
    const avg = parseFloat(avgOrderValue.replace(',', '.'));
    if (!hasLoggedRef.current && !isNaN(avg) && avg > 0) {
      hasLoggedRef.current = true;
      logToolUsage({ tool_name: 'shopify-calculator', locale });
    }
  }, [avgOrderValue, locale]);

  const plan = SHOPIFY_PLANS.find(p => p.id === selectedPlan) ?? SHOPIFY_PLANS[0];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const calculateResults = () => {
    const avg = parseFloat(avgOrderValue.replace(',', '.')) || 0;
    const orders = parseInt(monthlyOrders) || 0;
    const costPercent = parseFloat(purchaseCostPercent.replace(',', '.')) || 0;

    if (avg <= 0 || orders <= 0) return null;

    const monthlyRevenue = avg * orders;
    const planCost = billingCycle === 'yearly' ? plan.yearlyPricePerMonth : plan.monthlyPrice;

    let transactionFeePerOrder: number;
    let paymentProcessingPerOrder: number;

    if (useShopifyPayments) {
      transactionFeePerOrder = 0;
      paymentProcessingPerOrder = avg * plan.creditCardRateOnline + plan.creditCardFixedOnline;
    } else {
      transactionFeePerOrder = avg * plan.transactionFeeRate;
      paymentProcessingPerOrder = 0;
    }

    const totalFeesPerOrder = transactionFeePerOrder + paymentProcessingPerOrder;
    const monthlyTransactionFees = totalFeesPerOrder * orders;
    const totalMonthlyCost = planCost + monthlyTransactionFees;
    const costPercentAmount = (costPercent / 100) * avg;
    const profitPerOrder = avg - costPercentAmount - totalFeesPerOrder - (planCost / orders);
    const monthlyProfit = profitPerOrder * orders;
    const margin = (profitPerOrder / avg) * 100;

    return {
      planCost,
      transactionFeePerOrder,
      paymentProcessingPerOrder,
      totalFeesPerOrder,
      monthlyTransactionFees,
      totalMonthlyCost,
      monthlyRevenue,
      profitPerOrder,
      monthlyProfit,
      yearlyProfit: monthlyProfit * 12,
      yearlyCost: totalMonthlyCost * 12,
      margin,
      effectiveRate: monthlyRevenue > 0 ? (totalMonthlyCost / monthlyRevenue) * 100 : 0,
    };
  };

  const result = calculateResults();

  const reset = () => {
    setSelectedPlan('basic');
    setBillingCycle('monthly');
    setAvgOrderValue('');
    setMonthlyOrders('100');
    setUseShopifyPayments(true);
    setPurchaseCostPercent('');
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100">
          <Store className="h-7 w-7 text-green-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      {/* Calculator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Plan Selection */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.plan || 'Shopify Plan'}</label>
          <div className="grid grid-cols-3 gap-2">
            {SHOPIFY_PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedPlan(p.id)}
                className={`rounded-xl px-3 py-3 text-center transition-all ${
                  selectedPlan === p.id
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className="block text-sm font-medium">{p.name}</span>
                <span className={`block text-xs mt-0.5 ${selectedPlan === p.id ? 'text-brand-100' : 'text-slate-500'}`}>
                  €{billingCycle === 'yearly' ? p.yearlyPricePerMonth : p.monthlyPrice}/mo
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Billing Cycle */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.billingCycle || 'Factureringsperiode'}</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                billingCycle === 'monthly' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.monthly || 'Maandelijks'}
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                billingCycle === 'yearly' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.yearly || 'Jaarlijks (25% korting)'}
            </button>
          </div>
        </div>

        {/* Average Order Value */}
        <div className="mb-6">
          <label htmlFor="shopifyAvgOrder" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.avgOrderValue || 'Gemiddelde bestelwaarde'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="shopifyAvgOrder"
              value={avgOrderValue}
              onChange={(e) => setAvgOrderValue(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Monthly Orders */}
        <div className="mb-6">
          <label htmlFor="shopifyOrders" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.monthlyOrders || 'Bestellingen per maand'}
          </label>
          <input
            type="number"
            id="shopifyOrders"
            value={monthlyOrders}
            onChange={(e) => setMonthlyOrders(e.target.value)}
            min="0"
            placeholder="100"
            className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Purchase Cost % */}
        <div className="mb-6">
          <label htmlFor="shopifyCostPercent" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.purchaseCostPercent || 'Inkoopkosten (% van verkoopprijs)'}
          </label>
          <div className="relative">
            <input
              type="text"
              id="shopifyCostPercent"
              value={purchaseCostPercent}
              onChange={(e) => setPurchaseCostPercent(e.target.value)}
              placeholder="40"
              className="block w-full rounded-xl border border-slate-300 py-3 px-4 pr-8 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500">%</span>
          </div>
        </div>

        {/* Shopify Payments Toggle */}
        <div className="mb-6">
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <div>
              <span className="text-sm font-medium text-slate-700">{labels.shopifyPayments || 'Shopify Payments'}</span>
              <p className="text-xs text-slate-500 mt-0.5">
                {useShopifyPayments
                  ? `${(plan.creditCardRateOnline * 100).toFixed(1)}% + €${plan.creditCardFixedOnline.toFixed(2)}`
                  : `${(plan.transactionFeeRate * 100).toFixed(1)}% ${labels.transactionFee || 'transaction fee'}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUseShopifyPayments(!useShopifyPayments)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useShopifyPayments ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useShopifyPayments ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <ResultRow label={labels.planCost || 'Abonnementskosten per maand'} value={formatCurrency(result.planCost)} copyKey="planCost" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            {useShopifyPayments ? (
              <ResultRow label={labels.paymentProcessing || 'Payment processing per bestelling'} value={formatCurrency(result.paymentProcessingPerOrder)} copyKey="paymentFee" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            ) : (
              <ResultRow label={labels.transactionFeePerOrder || 'Transaction fee per bestelling'} value={formatCurrency(result.transactionFeePerOrder)} copyKey="transactionFee" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            )}
            <ResultRow label={labels.monthlyTransactionFees || 'Maandelijkse transactiekosten'} value={formatCurrency(result.monthlyTransactionFees)} copyKey="monthlyTxFees" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.totalMonthlyCost || 'Totale maandelijkse kosten'} value={formatCurrency(result.totalMonthlyCost)} copyKey="totalMonthlyCost" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{labels.effectiveRate || 'Effectief kostenpercentage'}</span>
                <span className="text-sm font-medium text-slate-600">{result.effectiveRate.toFixed(2)}%</span>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <ResultRow
                label={labels.profitPerOrder || 'Winst per bestelling'}
                value={formatCurrency(result.profitPerOrder)}
                copyKey="profitPerOrder"
                copied={copied}
                onCopy={copyToClipboard}
                bold
                valueColor={result.profitPerOrder >= 0 ? 'text-green-600' : 'text-red-600'}
                copyLabel={labels.copyResult}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{labels.margin || 'Marge'}</span>
                <span className={`text-sm font-medium ${result.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.margin.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2">
              <ResultRow label={labels.monthlyProfit || 'Maandelijkse winst'} value={formatCurrency(result.monthlyProfit)} copyKey="monthlyProfit" copied={copied} onCopy={copyToClipboard} valueColor={result.monthlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
              <ResultRow label={labels.yearlyProfit || 'Jaarlijkse winst'} value={formatCurrency(result.yearlyProfit)} copyKey="yearlyProfit" copied={copied} onCopy={copyToClipboard} bold valueColor={result.yearlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset || 'Reset'}
        </button>
      </div>

      {/* Data Updated Notice */}
      <div className="mt-3 text-center text-xs text-slate-400">
        {labels.dataUpdated || 'Tarieven bijgewerkt'}: {SHOPIFY_DATA_UPDATED}
      </div>

      {/* Plan Comparison Table */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.planComparison || 'Shopify plannen vergelijken'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">Plan</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.monthlyPriceLabel || 'Maandelijks'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.yearlyPriceLabel || 'Jaarlijks'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.cardRate || 'Kaart tarief'}</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.txFee || 'Ext. betaling'}</th>
              </tr>
            </thead>
            <tbody>
              {SHOPIFY_PLANS.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-700">{p.name}</td>
                  <td className="py-2 px-4 text-right text-slate-900">{formatCurrency(p.monthlyPrice)}</td>
                  <td className="py-2 px-4 text-right text-slate-900">{formatCurrency(p.yearlyPricePerMonth)}/mo</td>
                  <td className="py-2 px-4 text-right text-slate-600">{(p.creditCardRateOnline * 100).toFixed(1)}% + €{p.creditCardFixedOnline.toFixed(2)}</td>
                  <td className="py-2 pl-4 text-right text-slate-600">{(p.transactionFeeRate * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      <ToolSignupWall locale={locale} />

      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">{index + 1}</span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{content.disclaimer.heading}</h3>
          <p className="text-xs text-slate-500 leading-relaxed">{content.disclaimer.content}</p>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, value, copyKey, copied, onCopy, bold, valueColor, copyLabel }: {
  label: string;
  value: string;
  copyKey: string;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
  bold?: boolean;
  valueColor?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-medium text-slate-700' : 'text-slate-600'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`${bold ? 'text-lg font-semibold' : 'font-medium'} ${valueColor || 'text-slate-900'}`}>{value}</span>
        <button
          onClick={() => onCopy(value.replace(/[^0-9,.-]/g, ''), copyKey)}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          title={copyLabel || 'Copy'}
        >
          {copied === copyKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
